import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PortfolioTokenSelector from './PortfolioTokenSelector';
import { AgentAvatar, parseSeed, PALETTES } from './AgentAvatar';
import { useWallet } from '../hooks/useWallet';
import { useModalAlert } from './ModalAlert';

export const PortfolioSettings = ({
  portfolioId,
  config,
  onTokensChange = null,
  onAgentChange = null
}) => {
  const apiBaseUrl = config?.apiBaseUrl || window.location.origin.replace('/web', '');

  const { jwtToken } = useWallet();
  const { showSuccess, showError } = useModalAlert();
  const [userData, setUserData] = useState(null);
  const [portfolioTokens, setPortfolioTokens] = useState(null);

  // All agents available to assign (owned by current user)
  const [agents, setAgents] = useState([]);
  // Current assignments for this portfolio
  const [assignments, setAssignments] = useState([]);
  const [assigningAgent, setAssigningAgent] = useState(false);
  const [newAgentId, setNewAgentId] = useState('');

  // Invitations for this portfolio
  const [invitations, setInvitations] = useState([]);

  // Invite search state
  const [inviteQuery, setInviteQuery] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedInviteAgent, setSelectedInviteAgent] = useState(null);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchDebounceRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch portfolio config (tokens_selected) directly
  useEffect(() => {
    if (!jwtToken || !portfolioId) return;
    fetch(`${apiBaseUrl}/portfolio`, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((portfolios) => {
        const found = portfolios.find((p) => p.id === portfolioId || p.id === parseInt(portfolioId, 10));
        setPortfolioTokens(found?.tokens_selected ?? []);
      })
      .catch(() => setPortfolioTokens([]));
  }, [jwtToken, portfolioId, apiBaseUrl]);

  // Fetch user data for plan limits
  useEffect(() => {
    if (!jwtToken) { setUserData(null); return; }
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/user`, {
          headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        setUserData(await response.json());
      } catch (err) {
        console.error('Failed to fetch user data:', err);
        setUserData(null);
      } finally {
        // done
      }
    };
    fetchUserData();
  }, [jwtToken, apiBaseUrl]);

  // Fetch all user agents (owned by current user)
  useEffect(() => {
    if (!jwtToken) return;
    fetch(`${apiBaseUrl}/agents`, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then(setAgents)
      .catch(() => setAgents([]));
  }, [jwtToken, apiBaseUrl]);

  // Fetch current assignments for this portfolio
  const fetchAssignments = () => {
    if (!jwtToken || !portfolioId) return;
    fetch(`${apiBaseUrl}/portfolio/${portfolioId}/agents`, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then(setAssignments)
      .catch(() => setAssignments([]));
  };

  // Fetch invitations for this portfolio
  const fetchInvitations = () => {
    if (!jwtToken || !portfolioId) return;
    fetch(`${apiBaseUrl}/portfolio/${portfolioId}/agent-invitations`, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then(setInvitations)
      .catch(() => setInvitations([]));
  };

  useEffect(() => {
    fetchAssignments();
    fetchInvitations();
  }, [jwtToken, apiBaseUrl, portfolioId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced agent name search
  useEffect(() => {
    if (selectedInviteAgent) return; // already selected, don't re-search
    clearTimeout(searchDebounceRef.current);
    if (inviteQuery.length < 3) {
      setInviteSearchResults([]);
      setShowDropdown(false);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await fetch(
          `${apiBaseUrl}/agents/search?q=${encodeURIComponent(inviteQuery)}&portfolio_id=${portfolioId}`,
          { headers: { Authorization: `Bearer ${jwtToken}` } }
        );
        const data = res.ok ? await res.json() : [];
        setInviteSearchResults(data);
        setShowDropdown(data.length > 0);
      } catch {
        setInviteSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [inviteQuery, portfolioId, jwtToken, apiBaseUrl, selectedInviteAgent]);

  const handleAssignAgent = async (agentId, role = 'primary') => {
    if (!agentId) return;
    setAssigningAgent(true);
    try {
      const res = await fetch(`${apiBaseUrl}/portfolio/${portfolioId}/agent`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ agent_id: parseInt(agentId, 10), role }),
      });
      if (!res.ok) throw new Error(`Assignment failed (${res.status})`);
      showSuccess(role === 'primary' ? 'Primary agent set.' : 'Observer agent added.');
      setNewAgentId('');
      fetchAssignments();
      if (onAgentChange) onAgentChange();
    } catch (err) {
      showError('Failed to assign agent: ' + err.message);
    } finally {
      setAssigningAgent(false);
    }
  };

  const handlePromoteToPrimary = (agentId) => handleAssignAgent(agentId, 'primary');

  const handleRemoveAgent = async (agentId) => {
    if (!agentId) return;
    setAssigningAgent(true);
    try {
      const res = await fetch(`${apiBaseUrl}/portfolio/${portfolioId}/agent/${agentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) throw new Error(`Removal failed (${res.status})`);
      showSuccess('Agent removed.');
      fetchAssignments();
      if (onAgentChange) onAgentChange();
    } catch (err) {
      showError('Failed to remove agent: ' + err.message);
    } finally {
      setAssigningAgent(false);
    }
  };

  const handleSelectInviteAgent = (agent) => {
    setSelectedInviteAgent(agent);
    setInviteQuery(agent.name);
    setShowDropdown(false);
    setInviteSearchResults([]);
  };

  const handleSendInvite = async () => {
    if (!selectedInviteAgent) return;
    setIsSendingInvite(true);
    try {
      const res = await fetch(`${apiBaseUrl}/portfolio/${portfolioId}/agent-invitations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ agent_id: selectedInviteAgent.id, role: 'observer' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      showSuccess(`Invite sent to ${selectedInviteAgent.name}.`);
      setSelectedInviteAgent(null);
      setInviteQuery('');
      fetchInvitations();
    } catch (err) {
      showError('Failed to send invite: ' + err.message);
    } finally {
      setIsSendingInvite(false);
    }
  };

  // Agents not yet assigned to this portfolio (owned by current user)
  const assignedAgentIds = new Set(assignments.map((a) => a.agent_id));
  const unassignedAgents = agents.filter((a) => !assignedAgentIds.has(a.id));

  const inviteStatusBadge = (status) => {
    if (status === 'pending') return 'bg-yellow-900/40 text-yellow-300 border border-yellow-700/50';
    if (status === 'accepted') return 'bg-cyan-900/40 text-cyan-300 border border-cyan-700/50';
    return 'bg-gray-600/40 text-gray-400 border border-gray-600/50';
  };

  return (
    <div className="flex flex-col gap-6">

      {/* Assigned Agents */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">Assigned Agents</label>
        {assignments.length === 0 && invitations.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No agents assigned.</p>
        ) : (
          <div className="space-y-2 mb-3">
            {assignments.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between bg-gray-700/50 rounded px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  {(() => {
                    const isPrimary = a.role === 'primary';
                    const badge = (
                      <>
                        {a.agent?.avatar_seed ? (() => {
                          const { avatarSeed, paletteIndex } = parseSeed(a.agent.avatar_seed);
                          return (
                            <AgentAvatar
                              name={a.agent.name}
                              seed={avatarSeed}
                              colors={PALETTES[paletteIndex] || PALETTES[0]}
                              size={16}
                            />
                          );
                        })() : null}
                        <span className={`text-xs truncate ${isPrimary ? 'text-cyan-300' : 'text-gray-300'}`}>{a.agent?.name ?? `Agent #${a.agent_id}`}</span>
                      </>
                    );
                    return isPrimary ? (
                      <Link
                        to={`/agent/${a.agent_id}`}
                        className="flex items-center gap-1.5 bg-gray-700/60 rounded-full px-2 py-0.5 min-w-0 hover:bg-gray-600/60 transition-colors"
                      >
                        {badge}
                      </Link>
                    ) : (
                      <div className="flex items-center gap-1.5 bg-gray-700/60 rounded-full px-2 py-0.5 min-w-0">
                        {badge}
                      </div>
                    );
                  })()}
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                    a.role === 'primary'
                      ? 'bg-cyan-900/40 text-cyan-300 border border-cyan-700/50'
                      : 'bg-gray-600 text-gray-400'
                  }`}>
                    {a.role}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {a.role !== 'primary' && (
                    <button
                      onClick={() => handlePromoteToPrimary(a.agent_id)}
                      disabled={assigningAgent}
                      className="text-xs text-cyan-400 hover:text-cyan-300 disabled:opacity-50 transition-colors"
                    >
                      Make Primary
                    </button>
                  )}
                  <button
                    onClick={() => handleRemoveAgent(a.agent_id)}
                    disabled={assigningAgent}
                    className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            {/* Invitation rows */}
            {invitations.map((inv) => (
              <div
                key={`inv-${inv.id}`}
                className="flex items-center justify-between bg-gray-700/30 rounded px-3 py-2 border border-dashed border-gray-600/50"
              >
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-gray-700/60 rounded-full px-2 py-0.5 min-w-0">
                    {inv.agent?.avatar_seed ? (() => {
                      const { avatarSeed, paletteIndex } = parseSeed(inv.agent.avatar_seed);
                      return (
                        <AgentAvatar
                          name={inv.agent.name}
                          seed={avatarSeed}
                          colors={PALETTES[paletteIndex] || PALETTES[0]}
                          size={16}
                        />
                      );
                    })() : null}
                    <span className="text-xs text-gray-300 truncate">{inv.agent?.name ?? `Agent #${inv.agent_id}`}</span>
                  </div>
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${inviteStatusBadge(inv.status)}`}>
                    {inv.status}
                  </span>
                  <span className="text-xs text-gray-500">observer invite</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add agent dropdown (owned agents) */}
        {unassignedAgents.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={newAgentId}
              onChange={(e) => setNewAgentId(e.target.value)}
              disabled={assigningAgent}
              className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500 disabled:opacity-50"
            >
              <option value="" disabled>Add agent…</option>
              {unassignedAgents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <button
              onClick={() => handleAssignAgent(newAgentId, 'primary')}
              disabled={assigningAgent || !newAgentId}
              className="px-3 py-2 bg-cyan-700 hover:bg-cyan-600 disabled:opacity-50 rounded text-xs text-white transition-colors"
            >
              Assign Primary
            </button>
            <button
              onClick={() => handleAssignAgent(newAgentId, 'observer')}
              disabled={assigningAgent || !newAgentId}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded text-xs text-white transition-colors"
            >
              Add Observer
            </button>
            {assigningAgent && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-500" />
            )}
          </div>
        )}
      </div>

      {/* Invite External Observer Agent */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">Invite Observer Agent</label>
        <p className="text-xs text-gray-500 mb-2">
          Invite an agent owned by another user to observe this portfolio. They must accept the invite before observing.
        </p>
        <div className="relative" ref={dropdownRef}>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <input
                type="text"
                placeholder="Search agent by name…"
                value={inviteQuery}
                onChange={(e) => {
                  setInviteQuery(e.target.value);
                  if (selectedInviteAgent && e.target.value !== selectedInviteAgent.name) {
                    setSelectedInviteAgent(null);
                  }
                }}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500 placeholder-gray-500"
              />
              {isSearching && (
                <div className="absolute right-2 top-2.5 animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-500" />
              )}
            </div>
            <button
              onClick={handleSendInvite}
              disabled={isSendingInvite || !selectedInviteAgent}
              className="px-3 py-2 bg-yellow-700 hover:bg-yellow-600 disabled:opacity-50 rounded text-xs text-white transition-colors whitespace-nowrap"
            >
              {isSendingInvite ? 'Sending…' : 'Send Invite'}
            </button>
          </div>

          {/* Search dropdown */}
          {showDropdown && inviteSearchResults.length > 0 && (
            <div className="absolute z-20 mt-1 w-full max-w-xs bg-gray-800 border border-gray-600 rounded shadow-lg">
              {inviteSearchResults.map((agent) => (
                <button
                  key={agent.id}
                  onMouseDown={() => handleSelectInviteAgent(agent)}
                  className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-700 transition-colors first:rounded-t last:rounded-b"
                >
                  {agent.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Token Selector */}
      {portfolioTokens !== null && (
        <PortfolioTokenSelector
          portfolioId={portfolioId}
          config={config}
          authToken={jwtToken}
          initialSelectedTokens={portfolioTokens}
          onTokensChange={(tokens) => {
            setPortfolioTokens(tokens);
            if (onTokensChange) onTokensChange();
          }}
          userData={userData}
        />
      )}
    </div>
  );
};
